import clsx from 'clsx'
import compact from 'lodash/compact'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, {
  FC,
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { BaseFormInputProps } from '@Components/FormElements/UI/BaseFormInput'
import {
  SelectField,
  SelectFieldOption,
} from '@Components/FormElements/UI/SelectField'
import { SizeProps } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'

export enum SizeSelectorState {
  IDLE = 0,
  SIZE_AVAILABLE,
  SIZE_SOLD_OUT,
  MY_SIZE_DOESNT_EXIST,
}

type SizeSelectorProps = Omit<React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>, 'ref'>
& Pick<BaseFormInputProps, 'withoutLabelAnimation'>
& {
  error?: boolean
  hideMySizeIsNotHere?: boolean
  hideOutOfStockLabel?: boolean
  hidePlaceholder?: boolean
  onStateChange?: (state: SizeSelectorState) => void
  sizes: SizeProps[]
  unwrapped?: boolean
  value?: string
}

const MY_SIZE_IS_NOT_HERE = '🤷‍'

export const SizeSelector: FC<SizeSelectorProps> = forwardRef(
  (
    {
      error,
      hideMySizeIsNotHere,
      hideOutOfStockLabel,
      hidePlaceholder,
      onChange = noop,
      onStateChange = noop,
      sizes,
      value = '',
      unwrapped,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const handleValue = useCallback((selectedValue: string) => {
      if (selectedValue === MY_SIZE_IS_NOT_HERE) {
        onStateChange(SizeSelectorState.MY_SIZE_DOESNT_EXIST)
      } else {
        const currentSize = sizes.find(item => item.id === selectedValue)

        if (!currentSize) {
        // Something's wrong. Abort now!
          onStateChange(SizeSelectorState.IDLE)
          return
        }

        if (!currentSize.available) {
          onStateChange(SizeSelectorState.SIZE_SOLD_OUT)
        } else {
          onStateChange(SizeSelectorState.SIZE_AVAILABLE)
        }
      }
    }, [onStateChange, sizes])

    const localOnSelect: React.SelectHTMLAttributes<HTMLSelectElement>['onSelect'] = useCallback(
      (e) => {
        const { value: selectedValue } = e.target as HTMLSelectElement
        onChange(e)
        handleValue(selectedValue)
      },
      [handleValue, onChange],
    )

    useEffect(() => handleValue(value), [value, handleValue])

    const selectProps: React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> & { ref?: RefObject<HTMLSelectElement> } = {
      className: clsx({
        hasError: error,
      }),
      name: 'size',
      onChange: localOnSelect,
      ref: ref as RefObject<HTMLSelectElement>,
      value,
      ...props,
    }

    const options = useMemo((): (SelectFieldOption | boolean)[] => compact([
      ...map(
        sizes,
        ({ available, id, key, label }) => ({
          // "key" would not actually work as a fallback, here, but it's better than an empty string.
          key: id || key,
          value: `${label}${!available && !hideOutOfStockLabel ? ` - ${t('cms:SOLD_OUT_LABEL')}` : ''}`,
        }),
      ),
      !hideMySizeIsNotHere && { key: MY_SIZE_IS_NOT_HERE, value: t('cms:MY_SIZE_IS_NOT_HERE') },
    ]), [hideMySizeIsNotHere, hideOutOfStockLabel, sizes, t])

    return (
      <SelectField
        {...selectProps}
        allowEmptySelection
        id="sizeSelector"
        label={t('cms:SIZE')}
        options={options as SelectFieldOption[]}
        placeholder={!hidePlaceholder ? t('cms:SIZE').toUpperCase() : undefined}
      />
    )
  },
)
